import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, Server, Database, HardDrive, GitBranch, Clock,
  CheckCircle, AlertCircle, RefreshCw, Shield, FileText,
  Copy, Activity, Cpu, MemoryStick, Gauge, Eye, EyeOff,
  ChevronDown, ChevronRight, Terminal, Download, Upload,
  Layers, Settings, Box, History, Zap, MonitorCheck, Mail,
  ShieldAlert, Lock, Unlock, Globe, Ban, UserX, Bot, KeyRound,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const getHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ems_token')}`,
});

// Format uptime
const formatUptime = (seconds) => {
  if (!seconds) return '—';
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// Format bytes
const formatBytes = (mb) => {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  return `${mb.toFixed(1)} MB`;
};

// Gauge component
const CircleGauge = ({ value, label, color, size = 80 }) => {
  const radius = (size - 10) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  const strokeColor = value > 90 ? '#ef4444' : value > 70 ? '#f59e0b' : color;
  return (
    <div className="flex flex-col items-center gap-1.5">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke="currentColor" strokeWidth="4" className="text-muted" />
          <circle cx={size/2} cy={size/2} r={radius} fill="none" stroke={strokeColor} strokeWidth="4"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold">{Math.round(value)}%</span>
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
};

// Tab navigation
const TABS = [
  { id: 'overview', label: 'Overview', icon: MonitorCheck },
  { id: 'security', label: 'Security', icon: ShieldAlert },
  { id: 'database', label: 'Database', icon: Database },
  { id: 'migrations', label: 'Migrations', icon: GitBranch },
  { id: 'storage', label: 'Storage', icon: HardDrive },
  { id: 'logs', label: 'Logs', icon: Terminal },
  { id: 'environment', label: 'Environment', icon: Settings },
  { id: 'versions', label: 'Versions', icon: History },
  { id: 'reports', label: 'Reports', icon: Mail },
];

const HubSystemPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [logs, setLogs] = useState([]);
  const [logType, setLogType] = useState('all');  // 'all', 'backend', 'frontend'
  const [logLevel, setLogLevel] = useState('all'); // 'all', 'error', 'warning', 'info'
  const [logSearch, setLogSearch] = useState('');
  const [logStats, setLogStats] = useState(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [envSearch, setEnvSearch] = useState('');
  const [newVersion, setNewVersion] = useState('');
  const [versionNotes, setVersionNotes] = useState('');
  const [versionSaving, setVersionSaving] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [reportRecipients, setReportRecipients] = useState('info@spillconsult.com');
  const logEndRef = useRef(null);

  // Security panel state
  const [secStats, setSecStats] = useState(null);
  const [secLogs, setSecLogs] = useState([]);
  const [secLogFilter, setSecLogFilter] = useState('all');
  const [blacklistedIps, setBlacklistedIps] = useState([]);
  const [lockedAccounts, setLockedAccounts] = useState([]);
  const [secSettings, setSecSettings] = useState({ max_attempts: 10, lockout_minutes: 5, ip_blacklist_threshold: 10 });
  const [secLoading, setSecLoading] = useState(false);

  const fetchInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/hub/system-info`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load');
      setInfo(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (logLevel !== 'all') params.set('level', logLevel);
      if (logType !== 'all') params.set('source', logType);
      if (logSearch.trim()) params.set('search', logSearch.trim());
      const res = await fetch(`${API_URL}/hub/logs/app?${params}`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch {}
    finally { setLogsLoading(false); }
  }, [logType, logLevel, logSearch]);

  const fetchLogStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/hub/logs/stats`, { headers: getHeaders() });
      if (res.ok) setLogStats(await res.json());
    } catch {}
  }, []);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);
  useEffect(() => { if (activeTab === 'logs') { fetchLogs(); fetchLogStats(); } }, [activeTab, fetchLogs, fetchLogStats]);
  useEffect(() => { if (activeTab === 'security') fetchSecurityData(); }, [activeTab]);

  // Auto-refresh logs every 10 seconds when enabled
  useEffect(() => {
    if (!autoRefresh || activeTab !== 'logs') return;
    const interval = setInterval(() => { fetchLogs(); fetchLogStats(); }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, activeTab, fetchLogs, fetchLogStats]);

  const fetchSecurityData = async () => {
    setSecLoading(true);
    try {
      const [statsRes, logsRes, ipsRes, locksRes, settingsRes] = await Promise.all([
        fetch(`${API_URL}/hub/security/stats`, { headers: getHeaders() }),
        fetch(`${API_URL}/hub/security/logs?limit=100`, { headers: getHeaders() }),
        fetch(`${API_URL}/hub/security/blacklisted-ips`, { headers: getHeaders() }),
        fetch(`${API_URL}/hub/security/locked-accounts`, { headers: getHeaders() }),
        fetch(`${API_URL}/hub/security/settings`, { headers: getHeaders() }),
      ]);
      if (statsRes.ok) setSecStats(await statsRes.json());
      if (logsRes.ok) setSecLogs(await logsRes.json());
      if (ipsRes.ok) setBlacklistedIps(await ipsRes.json());
      if (locksRes.ok) setLockedAccounts(await locksRes.json());
      if (settingsRes.ok) setSecSettings(await settingsRes.json());
    } catch (err) { console.error(err); }
    finally { setSecLoading(false); }
  };

  const handleUnblockIp = async (ip) => {
    try {
      const res = await fetch(`${API_URL}/hub/security/unblock-ip`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ ip }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`IP ${ip} unblocked`);
      fetchSecurityData();
    } catch (err) { toast.error(err.message); }
  };

  const handleUnlockAccount = async (email) => {
    try {
      const res = await fetch(`${API_URL}/hub/security/unlock-account`, {
        method: 'POST', headers: getHeaders(), body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success(`Account ${email} unlocked`);
      fetchSecurityData();
    } catch (err) { toast.error(err.message); }
  };

  const handleSaveSecSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/hub/security/settings`, {
        method: 'PUT', headers: getHeaders(), body: JSON.stringify(secSettings),
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Security settings saved');
    } catch (err) { toast.error(err.message); }
  };

  const copyText = (text) => { navigator.clipboard.writeText(String(text)); toast.success('Copied'); };

  const recordVersion = async () => {
    if (!newVersion.trim()) { toast.error('Version number is required'); return; }
    setVersionSaving(true);
    try {
      const res = await fetch(`${API_URL}/hub/deploy-version`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ version: newVersion.trim(), notes: versionNotes.trim() }),
      });
      if (!res.ok) throw new Error('Failed to record version');
      toast.success(`Version v${newVersion.trim()} recorded`);
      setNewVersion('');
      setVersionNotes('');
      fetchInfo();
    } catch (err) { toast.error(err.message); }
    finally { setVersionSaving(false); }
  };

  const sendHealthReport = async () => {
    const emails = reportRecipients.split(',').map(e => e.trim()).filter(e => e.includes('@'));
    if (emails.length === 0) { toast.error('Enter at least one valid email'); return; }
    setSendingReport(true);
    try {
      const res = await fetch(`${API_URL}/hub/health-report/send`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ recipients: emails }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send report');
      toast.success(data.message || 'Report sent!');
      setReportPreview(data.data);
    } catch (err) { toast.error(err.message); }
    finally { setSendingReport(false); }
  };

  const previewReport = async () => {
    try {
      const res = await fetch(`${API_URL}/hub/health-report/preview`, { headers: getHeaders() });
      if (!res.ok) throw new Error('Failed to load preview');
      const data = await res.json();
      setReportPreview(data);
    } catch (err) { toast.error(err.message); }
  };

  // Theme styles
  const bg = isDark ? 'bg-[#06070a]' : 'bg-muted';
  const text = isDark ? 'text-white' : 'text-foreground';
  const textMuted = isDark ? 'text-white/40' : 'text-muted-foreground';
  const border = isDark ? 'border-white/[0.06]' : 'border-border';
  const cardBg = isDark ? 'bg-white/[0.02]' : 'bg-white';
  const statBg = isDark ? 'bg-white/[0.03]' : 'bg-muted';

  const InfoRow = ({ label, value, mono = false, secret = false }) => (
    <div className={`flex items-center justify-between py-2.5 border-b ${border} last:border-0`}>
      <span className={`text-sm ${textMuted}`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-sm ${mono ? 'font-mono text-blue-400' : ''}`}>
          {secret && !showSecrets ? '••••••••' : value}
        </span>
        {mono && (
          <button onClick={() => copyText(value)}
            className={`p-1 rounded ${isDark ? 'hover:bg-white/5 text-white/20 hover:text-white/60' : 'hover:bg-muted text-muted-foreground hover:text-muted-foreground'} transition`}>
            <Copy className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={`min-h-screen ${bg} ${text} flex items-center justify-center`}>
        <RefreshCw className={`w-6 h-6 animate-spin ${textMuted}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg} ${text}`} data-testid="hub-system-page">
      {/* Top Bar */}
      <div className={`flex items-center justify-between px-6 py-4 border-b ${border}`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/hub')}
            className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/5 text-white/40 hover:text-white' : 'hover:bg-muted text-muted-foreground hover:text-foreground'} transition-colors`}
            data-testid="system-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Server className="w-5 h-5 text-blue-400" />
          <span className="text-lg font-semibold">System Management</span>
        </div>
        <div className="flex items-center gap-3">
          {info && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'} border`}>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="text-xs text-emerald-400 font-medium">v{info.version}</span>
            </div>
          )}
          <Button variant="outline" size="sm" onClick={fetchInfo}
            className={`h-8 ${isDark ? 'border-white/10 text-white/60 hover:bg-white/5' : ''}`}
            data-testid="refresh-system-info">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-65px)]">
        {/* Sidebar Tabs */}
        <div className={`w-52 border-r ${border} py-4 px-2 flex-shrink-0`}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm mb-1 transition-all ${
                activeTab === tab.id
                  ? isDark ? 'bg-white/[0.06] text-white' : 'bg-blue-50 text-blue-700'
                  : isDark ? 'text-white/40 hover:text-white/60 hover:bg-white/[0.03]' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
              data-testid={`system-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Main Content */}
        <ScrollArea className="flex-1">
          <div className="max-w-5xl mx-auto px-6 py-6 space-y-6">

            {/* OVERVIEW TAB */}
            {activeTab === 'overview' && info && (
              <>
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="system-stats">
                  {[
                    { icon: Shield, label: 'Users', value: info.database.users, color: 'text-blue-400' },
                    { icon: Activity, label: 'EMS Exercises', value: info.database.exercises, color: 'text-emerald-400' },
                    { icon: FileText, label: 'MP Posts', value: info.database.posts, color: 'text-purple-400' },
                    { icon: HardDrive, label: 'Upload Files', value: info.storage.upload_files, color: 'text-amber-400' },
                  ].map(s => (
                    <div key={s.label} className={`p-4 rounded-xl ${statBg} border ${border}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <s.icon className={`w-4 h-4 ${s.color}`} />
                        <span className={`text-xs ${textMuted}`}>{s.label}</span>
                      </div>
                      <p className="text-2xl font-bold">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* System Health Gauges */}
                <div className={`rounded-xl border ${border} ${cardBg} p-6`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-5 flex items-center gap-2`}>
                    <Gauge className="w-4 h-4" /> System Health
                  </h2>
                  <div className="grid grid-cols-3 gap-8 place-items-center">
                    <div className="relative flex flex-col items-center">
                      <CircleGauge value={info.system_health.cpu_percent} label="CPU" color="#3b82f6" />
                    </div>
                    <div className="relative flex flex-col items-center">
                      <CircleGauge value={info.system_health.memory_percent} label="Memory" color="#8b5cf6" />
                      <span className="text-[10px] text-muted-foreground mt-0.5">
                        {info.system_health.memory_used_gb} / {info.system_health.memory_total_gb} GB
                      </span>
                    </div>
                    <div className="relative flex flex-col items-center">
                      <CircleGauge value={info.system_health.disk_percent} label="Disk" color="#f59e0b" />
                      <span className="text-[10px] text-muted-foreground mt-0.5">
                        {info.system_health.disk_used_gb} / {info.system_health.disk_total_gb} GB
                      </span>
                    </div>
                  </div>
                </div>

                {/* Application Info */}
                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden px-5`}>
                  <div className={`py-3 border-b ${border}`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <Box className="w-4 h-4" /> Application
                    </h2>
                  </div>
                  <InfoRow label="App Name" value={info.app_name} />
                  <InfoRow label="Version" value={info.version} mono />
                  <InfoRow label="Build Date" value={info.build_date} mono />
                  <InfoRow label="Python" value={info.python_version} mono />
                  <InfoRow label="OS" value={info.os_info} />
                  <InfoRow label="Uptime" value={formatUptime(info.uptime_seconds)} />
                  <InfoRow label="Server Time (UTC)" value={info.server_time ? new Date(info.server_time).toLocaleString() : '—'} />
                </div>

                {/* Deployment Quick Reference */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-4 flex items-center gap-2`}>
                    <Zap className="w-4 h-4" /> Deployment Quick Reference
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { icon: Download, title: 'Deploy Update', cmd: './scripts/deploy.sh', desc: 'Pull → build → migrate → restart' },
                      { icon: Database, title: 'Backup', cmd: './scripts/backup-db.sh', desc: 'Create timestamped MongoDB backup' },
                      { icon: GitBranch, title: 'Rollback', cmd: './scripts/rollback.sh', desc: 'Git revert → rebuild containers' },
                      { icon: Terminal, title: 'View Logs', cmd: 'docker compose logs -f', desc: 'Stream container logs' },
                      { icon: Layers, title: 'Migrations', cmd: 'python -m migrations.runner', desc: 'Run pending DB migrations' },
                      { icon: History, title: 'Migration Status', cmd: 'python -m migrations.runner --status', desc: 'Show applied/pending' },
                    ].map(({ icon: Icon, title, cmd, desc }) => (
                      <div key={title} className={`p-3 rounded-lg ${statBg} border ${border}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`w-3.5 h-3.5 ${textMuted}`} />
                          <span className="text-sm font-medium">{title}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <code className={`text-xs font-mono px-2 py-1 rounded ${isDark ? 'bg-white/5 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>{cmd}</code>
                          <button onClick={() => copyText(cmd)}
                            className={`p-1 rounded ${isDark ? 'hover:bg-white/5 text-white/20' : 'hover:bg-muted text-muted-foreground'} transition`}>
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <p className={`text-[11px] ${textMuted} mt-1`}>{desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* SECURITY TAB */}
            {activeTab === 'security' && (
              <>
                {/* Security Stats */}
                {secStats && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="security-stats">
                    {[
                      { icon: Activity, label: 'Total Events', value: secStats.total_events, color: 'text-blue-400' },
                      { icon: Lock, label: 'Failed Logins', value: secStats.failed_logins, color: 'text-red-400' },
                      { icon: CheckCircle, label: 'Successful', value: secStats.successful_logins, color: 'text-green-400' },
                      { icon: Ban, label: 'Blocked IPs', value: secStats.blocked_ips, color: 'text-orange-400' },
                      { icon: UserX, label: 'Locked Accts', value: secStats.locked_accounts, color: 'text-yellow-400' },
                      { icon: Bot, label: 'Bots Detected', value: secStats.bots_detected, color: 'text-purple-400' },
                    ].map(s => (
                      <div key={s.label} className={`p-3 rounded-xl ${statBg} border ${border}`}>
                        <div className="flex items-center gap-1.5 mb-1">
                          <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
                          <span className={`text-[10px] ${textMuted}`}>{s.label}</span>
                        </div>
                        <p className="text-xl font-bold">{s.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Security Settings */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-4 flex items-center gap-2`}>
                    <Settings className="w-4 h-4" /> Security Configuration
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className={`text-xs ${textMuted} mb-1 block`}>Max Login Attempts</label>
                      <Input
                        type="number" min={3} max={50}
                        value={secSettings.max_attempts}
                        onChange={e => setSecSettings({ ...secSettings, max_attempts: parseInt(e.target.value) || 10 })}
                        data-testid="sec-max-attempts"
                      />
                      <p className={`text-[10px] ${textMuted} mt-1`}>Lock account after this many failures</p>
                    </div>
                    <div>
                      <label className={`text-xs ${textMuted} mb-1 block`}>Lockout Duration (min)</label>
                      <Input
                        type="number" min={1} max={60}
                        value={secSettings.lockout_minutes}
                        onChange={e => setSecSettings({ ...secSettings, lockout_minutes: parseInt(e.target.value) || 5 })}
                        data-testid="sec-lockout-minutes"
                      />
                      <p className={`text-[10px] ${textMuted} mt-1`}>Minutes before auto-unlock</p>
                    </div>
                    <div>
                      <label className={`text-xs ${textMuted} mb-1 block`}>IP Block Threshold</label>
                      <Input
                        type="number" min={5} max={100}
                        value={secSettings.ip_blacklist_threshold}
                        onChange={e => setSecSettings({ ...secSettings, ip_blacklist_threshold: parseInt(e.target.value) || 10 })}
                        data-testid="sec-ip-threshold"
                      />
                      <p className={`text-[10px] ${textMuted} mt-1`}>Failed attempts before IP block</p>
                    </div>
                  </div>
                  <Button onClick={handleSaveSecSettings} size="sm" className="mt-4" data-testid="save-sec-settings">
                    Save Settings
                  </Button>
                </div>

                {/* Blocked IPs & Locked Accounts */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Blocked IPs */}
                  <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                    <h2 className={`text-sm font-medium ${textMuted} mb-3 flex items-center gap-2`}>
                      <Globe className="w-4 h-4" /> Blocked IPs
                      <Badge variant="secondary" className="text-[10px]">{blacklistedIps.length}</Badge>
                    </h2>
                    {blacklistedIps.length === 0 ? (
                      <p className={`text-xs ${textMuted} py-4 text-center`}>No blocked IPs</p>
                    ) : (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {blacklistedIps.map((ip, i) => (
                          <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${statBg} border ${border}`}>
                            <div>
                              <p className="text-sm font-mono">{ip.ip}</p>
                              <p className={`text-[10px] ${textMuted}`}>{ip.reason || 'Auto-blocked'}</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handleUnblockIp(ip.ip)} className="h-7 text-xs" data-testid={`unblock-${ip.ip}`}>
                              <Unlock className="w-3 h-3 mr-1" /> Unblock
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Locked Accounts */}
                  <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                    <h2 className={`text-sm font-medium ${textMuted} mb-3 flex items-center gap-2`}>
                      <Lock className="w-4 h-4" /> Locked Accounts
                      <Badge variant="secondary" className="text-[10px]">{lockedAccounts.length}</Badge>
                    </h2>
                    {lockedAccounts.length === 0 ? (
                      <p className={`text-xs ${textMuted} py-4 text-center`}>No locked accounts</p>
                    ) : (
                      <div className="space-y-2 max-h-[250px] overflow-y-auto">
                        {lockedAccounts.map((acc, i) => (
                          <div key={i} className={`flex items-center justify-between px-3 py-2 rounded-lg ${statBg} border ${border}`}>
                            <div>
                              <p className="text-sm">{acc.email}</p>
                              <p className={`text-[10px] ${textMuted}`}>Unlocks in {Math.ceil(acc.remaining_seconds / 60)}m</p>
                            </div>
                            <Button size="sm" variant="outline" onClick={() => handleUnlockAccount(acc.email)} className="h-7 text-xs" data-testid={`unlock-${acc.email}`}>
                              <Unlock className="w-3 h-3 mr-1" /> Unlock
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Security Audit Log */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <div className="flex items-center justify-between mb-3">
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <FileText className="w-4 h-4" /> Security Audit Log
                      <Badge variant="secondary" className="text-[10px]">{secLogs.length}</Badge>
                    </h2>
                    <div className="flex items-center gap-2">
                      <Link
                        to="/hub/upload-quarantine"
                        className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20 transition-colors flex items-center gap-1"
                        data-testid="link-upload-quarantine"
                        title="View files rejected by the upload validator"
                      >
                        <FileText className="w-2.5 h-2.5" /> Upload Quarantine
                      </Link>
                      <div className="flex items-center gap-1">
                      {['all', 'login_failed', 'login_success', 'account_locked', 'ip_blacklisted', 'bot_detected'].map(f => (
                        <button
                          key={f}
                          onClick={() => setSecLogFilter(f)}
                          className={`text-[10px] px-2 py-0.5 rounded-full transition-colors ${
                            secLogFilter === f
                              ? 'bg-primary text-primary-foreground font-medium'
                              : `${statBg} ${textMuted} hover:text-foreground`
                          }`}
                          data-testid={`sec-log-filter-${f}`}
                        >
                          {f === 'all' ? 'All' : f.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </button>
                      ))}
                      </div>
                    </div>
                  </div>
                  <ScrollArea className="h-[350px]">
                    <div className="space-y-1.5">
                      {(secLogFilter === 'all' ? secLogs : secLogs.filter(l => l.event_type === secLogFilter)).map((log, i) => {
                        const isSuccess = log.success;
                        const isBad = ['login_failed', 'account_locked', 'ip_blacklisted', 'bot_detected', 'blocked_ip_attempt'].includes(log.event_type);
                        return (
                          <div key={i} className={`px-3 py-2 rounded-lg border text-xs ${
                            isSuccess ? `border-green-200 dark:border-green-900/30 bg-green-50 dark:bg-green-900/10`
                            : isBad ? `border-red-200 dark:border-red-900/30 bg-red-50 dark:bg-red-900/10`
                            : `${border} ${statBg}`
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {isSuccess ? <CheckCircle className="w-3 h-3 text-green-500" /> : isBad ? <AlertCircle className="w-3 h-3 text-red-500" /> : <Activity className="w-3 h-3 text-blue-500" />}
                                <span className="font-medium capitalize">{log.event_type?.replace(/_/g, ' ')}</span>
                                {log.email && <span className={`${textMuted}`}>{log.email}</span>}
                              </div>
                              <span className={`${textMuted} font-mono text-[10px]`}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : ''}</span>
                            </div>
                            <div className={`mt-1 flex items-center gap-3 ${textMuted}`}>
                              <span className="font-mono">{log.ip}</span>
                              {log.details && <span>{log.details}</span>}
                            </div>
                          </div>
                        );
                      })}
                      {secLogs.length === 0 && (
                        <p className={`text-center py-8 ${textMuted} text-sm`}>No security events recorded yet</p>
                      )}
                    </div>
                  </ScrollArea>
                </div>

                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={fetchSecurityData} data-testid="refresh-security">
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
                  </Button>
                </div>
              </>
            )}

            {/* DATABASE TAB */}
            {activeTab === 'database' && info && (
              <>
                {/* DB Status */}
                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden px-5`}>
                  <div className={`py-3 border-b ${border}`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <Database className="w-4 h-4" /> Connection
                    </h2>
                  </div>
                  <div className={`flex items-center justify-between py-2.5 border-b ${border}`}>
                    <span className={`text-sm ${textMuted}`}>Status</span>
                    <div className="flex items-center gap-2">
                      {info.database.status === 'connected' ? (
                        <><CheckCircle className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-400">Connected</span></>
                      ) : (
                        <><AlertCircle className="w-4 h-4 text-red-400" /><span className="text-sm text-red-400">Error</span></>
                      )}
                    </div>
                  </div>
                  <InfoRow label="Users" value={info.database.users} />
                  <InfoRow label="EMS Exercises" value={info.database.exercises} />
                  <InfoRow label="MP Exercises" value={info.database.mp_exercises} />
                  <InfoRow label="Media Posts" value={info.database.posts} />
                  <InfoRow label="Platforms" value={info.database.platforms} />
                  <InfoRow label="Audit Logs" value={info.database.audit_logs} />
                  <InfoRow label="Timeline Events" value={info.database.timeline_events} />
                  <InfoRow label="Activity Logs" value={info.database.activity_logs} />
                </div>

                {/* All Collections */}
                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
                  <div className={`px-5 py-3 border-b ${border}`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <Layers className="w-4 h-4" /> All Collections ({info.collections?.length || 0})
                    </h2>
                  </div>
                  <div className="max-h-[400px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-muted'}`}>
                          <th className={`text-left px-5 py-2 text-xs font-medium ${textMuted} uppercase tracking-wider`}>Collection</th>
                          <th className={`text-right px-5 py-2 text-xs font-medium ${textMuted} uppercase tracking-wider`}>Documents</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(info.collections || []).map(col => (
                          <tr key={col.name} className={`border-b ${border} last:border-0`}>
                            <td className="px-5 py-2 font-mono text-xs">{col.name}</td>
                            <td className="px-5 py-2 text-right">
                              <Badge variant={col.count > 0 ? 'default' : 'secondary'}
                                className={`text-[10px] ${col.count > 100 ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : col.count > 0 ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-muted0/10 text-muted-foreground border-gray-500/20'} border`}>
                                {col.count.toLocaleString()}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* MIGRATIONS TAB */}
            {activeTab === 'migrations' && info && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-4 rounded-xl ${statBg} border ${border}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className={`text-xs ${textMuted}`}>Applied</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-400">{info.migrations.applied}</p>
                  </div>
                  <div className={`p-4 rounded-xl ${statBg} border ${border}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <span className={`text-xs ${textMuted}`}>Pending</span>
                    </div>
                    <p className="text-2xl font-bold text-amber-400">{info.migrations.pending}</p>
                  </div>
                  <div className={`p-4 rounded-xl ${statBg} border ${border}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Layers className="w-4 h-4 text-blue-400" />
                      <span className={`text-xs ${textMuted}`}>Total</span>
                    </div>
                    <p className="text-2xl font-bold">{info.migrations.all?.length || 0}</p>
                  </div>
                </div>

                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
                  <div className={`px-5 py-3 border-b ${border} flex items-center justify-between`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <GitBranch className="w-4 h-4" /> Migration History
                    </h2>
                    <div className="flex gap-2">
                      <code className={`text-xs font-mono px-2 py-1 rounded ${isDark ? 'bg-white/5 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>
                        python -m migrations.runner
                      </code>
                      <button onClick={() => copyText('python -m migrations.runner')}
                        className={`p-1 rounded ${isDark ? 'hover:bg-white/5 text-white/20' : 'hover:bg-muted text-muted-foreground'} transition`}>
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="px-5 py-1">
                    {(info.migrations.all || []).map((m, i) => (
                      <div key={m.name} className={`flex items-center gap-3 py-3 ${i < (info.migrations.all || []).length - 1 ? `border-b ${border}` : ''}`}>
                        <div className="relative">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            m.status === 'applied' ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                          }`}>
                            {m.status === 'applied' ? (
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                            ) : (
                              <Clock className="w-4 h-4 text-amber-400" />
                            )}
                          </div>
                          {i < (info.migrations.all || []).length - 1 && (
                            <div className={`absolute top-8 left-1/2 -translate-x-1/2 w-px h-6 ${
                              m.status === 'applied' ? 'bg-emerald-500/20' : 'bg-card'
                            }`} />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-mono text-sm">{m.name}</p>
                          <p className={`text-xs ${textMuted}`}>
                            {m.status === 'applied' && m.applied_at ? `Applied ${new Date(m.applied_at).toLocaleString()}` : 'Pending'}
                          </p>
                        </div>
                        <Badge className={`text-[10px] border ${
                          m.status === 'applied' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                        }`}>{m.status}</Badge>
                      </div>
                    ))}
                    {(!info.migrations.all || info.migrations.all.length === 0) && (
                      <div className="py-8 text-center">
                        <GitBranch className={`w-8 h-8 mx-auto mb-2 ${textMuted} opacity-30`} />
                        <p className={`text-sm ${textMuted}`}>No migrations found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Rollback Instructions */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-3 flex items-center gap-2`}>
                    <History className="w-4 h-4" /> Rollback Commands
                  </h2>
                  <div className="space-y-2">
                    {[
                      { label: 'Check status', cmd: 'python -m migrations.runner --status' },
                      { label: 'Rollback last 1', cmd: 'python -m migrations.runner --rollback 1' },
                      { label: 'Rollback last 2', cmd: 'python -m migrations.runner --rollback 2' },
                    ].map(item => (
                      <div key={item.cmd} className="flex items-center justify-between">
                        <span className={`text-sm ${textMuted}`}>{item.label}</span>
                        <div className="flex items-center gap-2">
                          <code className={`text-xs font-mono px-2 py-1 rounded ${isDark ? 'bg-white/5 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>{item.cmd}</code>
                          <button onClick={() => copyText(item.cmd)}
                            className={`p-1 rounded ${isDark ? 'hover:bg-white/5 text-white/20' : 'hover:bg-muted text-muted-foreground'} transition`}>
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* STORAGE TAB */}
            {activeTab === 'storage' && info && (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className={`p-4 rounded-xl ${statBg} border ${border}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <HardDrive className="w-4 h-4 text-amber-400" />
                      <span className={`text-xs ${textMuted}`}>Files</span>
                    </div>
                    <p className="text-2xl font-bold">{info.storage.upload_files}</p>
                  </div>
                  <div className={`p-4 rounded-xl ${statBg} border ${border}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Upload className="w-4 h-4 text-blue-400" />
                      <span className={`text-xs ${textMuted}`}>Storage Used</span>
                    </div>
                    <p className="text-2xl font-bold">{formatBytes(info.storage.upload_size_mb)}</p>
                  </div>
                  <div className={`p-4 rounded-xl ${statBg} border ${border}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <Database className="w-4 h-4 text-purple-400" />
                      <span className={`text-xs ${textMuted}`}>Backups</span>
                    </div>
                    <p className="text-2xl font-bold">{info.backups?.length || 0}</p>
                  </div>
                </div>

                {/* Largest Files */}
                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
                  <div className={`px-5 py-3 border-b ${border}`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <FileText className="w-4 h-4" /> Largest Uploads
                    </h2>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {(info.storage.largest_files || []).length > 0 ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-muted'}`}>
                            <th className={`text-left px-5 py-2 text-xs font-medium ${textMuted} uppercase`}>File</th>
                            <th className={`text-right px-5 py-2 text-xs font-medium ${textMuted} uppercase`}>Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(info.storage.largest_files || []).map(f => (
                            <tr key={f.name} className={`border-b ${border} last:border-0`}>
                              <td className="px-5 py-2 font-mono text-xs truncate max-w-[300px]">{f.name}</td>
                              <td className="px-5 py-2 text-right text-xs text-muted-foreground">{f.size_kb > 1024 ? `${(f.size_kb / 1024).toFixed(1)} MB` : `${f.size_kb} KB`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-8 text-center"><p className={`text-sm ${textMuted}`}>No uploaded files</p></div>
                    )}
                  </div>
                </div>

                {/* Backups */}
                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
                  <div className={`px-5 py-3 border-b ${border} flex items-center justify-between`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <Download className="w-4 h-4" /> Database Backups
                    </h2>
                    <div className="flex gap-2">
                      <code className={`text-xs font-mono px-2 py-1 rounded ${isDark ? 'bg-white/5 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>./scripts/backup-db.sh</code>
                      <button onClick={() => copyText('./scripts/backup-db.sh')}
                        className={`p-1 rounded ${isDark ? 'hover:bg-white/5 text-white/20' : 'hover:bg-muted text-muted-foreground'} transition`}>
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {(info.backups || []).length > 0 ? (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={`border-b ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-muted'}`}>
                            <th className={`text-left px-5 py-2 text-xs font-medium ${textMuted} uppercase`}>Backup</th>
                            <th className={`text-right px-5 py-2 text-xs font-medium ${textMuted} uppercase`}>Size</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(info.backups || []).map(b => (
                            <tr key={b.name} className={`border-b ${border} last:border-0`}>
                              <td className="px-5 py-2 font-mono text-xs">{b.name}</td>
                              <td className="px-5 py-2 text-right text-xs text-muted-foreground">{b.size_mb} MB</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="py-8 text-center">
                        <p className={`text-sm ${textMuted}`}>No backups found locally</p>
                        <p className={`text-xs ${textMuted} mt-1`}>Backups are created automatically on your IONOS VPS</p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* LOGS TAB */}
            {activeTab === 'logs' && (
              <>
                {/* Log Stats */}
                {logStats && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="log-stats">
                    {[
                      { label: 'Total Logs', value: logStats.total, color: 'text-blue-400' },
                      { label: 'Last Hour', value: logStats.last_1h, color: 'text-emerald-400' },
                      { label: 'Last 24h', value: logStats.last_24h, color: 'text-purple-400' },
                      { label: 'Errors (24h)', value: logStats.errors_24h, color: logStats.errors_24h > 0 ? 'text-red-400' : 'text-emerald-400' },
                      { label: 'Warnings', value: logStats.by_level?.warning || 0, color: 'text-amber-400' },
                    ].map(s => (
                      <div key={s.label} className={`p-3 rounded-xl ${statBg} border ${border}`}>
                        <span className={`text-[10px] ${textMuted} block mb-0.5`}>{s.label}</span>
                        <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Filters */}
                <div className="flex items-center gap-3 flex-wrap">
                  <Select value={logLevel} onValueChange={v => setLogLevel(v)}>
                    <SelectTrigger className={`w-32 ${isDark ? 'bg-white/[0.03] border-white/10 text-white' : ''}`} data-testid="log-level-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Levels</SelectItem>
                      <SelectItem value="error">Errors</SelectItem>
                      <SelectItem value="warning">Warnings</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={logType} onValueChange={v => setLogType(v)}>
                    <SelectTrigger className={`w-36 ${isDark ? 'bg-white/[0.03] border-white/10 text-white' : ''}`} data-testid="log-source-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="backend">Backend</SelectItem>
                      <SelectItem value="frontend">Frontend</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    placeholder="Search logs..."
                    value={logSearch}
                    onChange={e => setLogSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchLogs()}
                    className={`flex-1 min-w-[150px] h-9 ${isDark ? 'bg-white/[0.03] border-white/10 text-white placeholder:text-white/20' : ''}`}
                    data-testid="log-search-input"
                  />
                  <Button variant="outline" size="sm" onClick={() => { fetchLogs(); fetchLogStats(); }} disabled={logsLoading}
                    className={`h-9 ${isDark ? 'border-white/10 text-white/60 hover:bg-white/5' : ''}`} data-testid="refresh-logs-btn">
                    {logsLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    <span className="ml-1.5">Refresh</span>
                  </Button>
                  <Button
                    variant={autoRefresh ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={`h-9 ${autoRefresh ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : isDark ? 'border-white/10 text-white/60 hover:bg-white/5' : ''}`}
                    data-testid="auto-refresh-btn"
                  >
                    <Activity className="w-3.5 h-3.5 mr-1" />
                    {autoRefresh ? 'Live' : 'Auto'}
                  </Button>
                  <span className={`text-xs ${textMuted}`}>{logs.length} entries</span>
                </div>

                {/* Log Entries */}
                <div className={`rounded-xl border ${border} overflow-hidden`}>
                  <div className={`h-[500px] overflow-y-auto p-4 font-mono text-xs ${isDark ? 'bg-black/40' : 'bg-card'} text-muted-foreground`} data-testid="system-log-viewer">
                    {logs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                        <Terminal className="w-8 h-8 mb-2 opacity-30" />
                        <p className="text-sm">No logs available</p>
                        <p className="text-[11px] mt-1 opacity-60">Logs are captured automatically from API requests, errors, and timeouts.</p>
                      </div>
                    ) : (
                      logs.map((log, i) => (
                        <div key={i} className={`flex gap-2 py-1 px-2 rounded mb-0.5 leading-relaxed ${
                          log.level === 'error' ? 'bg-red-500/5 hover:bg-red-500/10' :
                          log.level === 'warning' ? 'bg-amber-500/5 hover:bg-amber-500/10' :
                          'hover:bg-white/[0.02]'
                        }`}>
                          <span className={`shrink-0 w-[55px] text-right ${
                            log.level === 'error' ? 'text-red-400 font-semibold' :
                            log.level === 'warning' ? 'text-amber-400' :
                            'text-blue-300'
                          }`}>
                            {log.level?.toUpperCase()?.slice(0, 5)}
                          </span>
                          <span className="shrink-0 w-[140px] text-muted-foreground">
                            {log.timestamp ? new Date(log.timestamp).toLocaleString(undefined, { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}
                          </span>
                          <span className={`shrink-0 w-[70px] ${log.source?.startsWith('frontend') ? 'text-purple-400' : 'text-cyan-400'}`}>
                            {log.source?.replace('backend', 'BE').replace('frontend', 'FE')?.slice(0, 10)}
                          </span>
                          {log.status_code && (
                            <span className={`shrink-0 w-[30px] ${log.status_code >= 500 ? 'text-red-400' : log.status_code >= 400 ? 'text-amber-400' : 'text-emerald-400'}`}>
                              {log.status_code}
                            </span>
                          )}
                          {log.duration_ms != null && (
                            <span className={`shrink-0 w-[55px] text-right ${log.duration_ms > 5000 ? 'text-red-400' : log.duration_ms > 1000 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                              {log.duration_ms > 1000 ? `${(log.duration_ms / 1000).toFixed(1)}s` : `${Math.round(log.duration_ms)}ms`}
                            </span>
                          )}
                          <span className="flex-1 break-all">{log.message}</span>
                          {log.ip && <span className="shrink-0 text-muted-foreground/50 ml-1">{log.ip}</span>}
                        </div>
                      ))
                    )}
                    <div ref={logEndRef} />
                  </div>
                </div>

                {/* Clear Logs */}
                <div className="flex items-center justify-between">
                  <p className={`text-xs ${textMuted}`}>
                    Logs are stored in the database and persist across deployments. Oldest logs are auto-pruned at 50,000 entries.
                  </p>
                  <Button variant="outline" size="sm"
                    className={`text-xs ${isDark ? 'border-white/10 text-white/40 hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20' : 'hover:bg-red-50 hover:text-red-600 hover:border-red-200'}`}
                    onClick={async () => {
                      if (window.confirm('Clear all logs? This cannot be undone.')) {
                        try {
                          await fetch(`${API_URL}/hub/logs/clear?older_than_days=0`, { method: 'DELETE', headers: getHeaders() });
                          toast.success('Logs cleared');
                          fetchLogs(); fetchLogStats();
                        } catch { toast.error('Failed to clear logs'); }
                      }
                    }}
                    data-testid="clear-logs-btn"
                  >
                    Clear All
                  </Button>
                </div>
              </>
            )}

            {/* ENVIRONMENT TAB */}
            {activeTab === 'environment' && info && (
              <>
                <div className="flex items-center gap-3">
                  <div className="relative flex-1">
                    <Input placeholder="Search variables..." value={envSearch}
                      onChange={e => setEnvSearch(e.target.value)}
                      className={`${isDark ? 'bg-white/[0.03] border-white/10 text-white placeholder:text-white/20' : ''}`}
                      data-testid="env-search" />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setShowSecrets(!showSecrets)}
                    className={`h-9 ${isDark ? 'border-white/10 text-white/60 hover:bg-white/5' : ''}`}
                    data-testid="toggle-secrets-btn">
                    {showSecrets ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
                    {showSecrets ? 'Hide' : 'Show'} Secrets
                  </Button>
                </div>

                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
                  <div className="max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={`border-b ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-muted'} sticky top-0`}>
                          <th className={`text-left px-5 py-2 text-xs font-medium ${textMuted} uppercase`}>Variable</th>
                          <th className={`text-left px-5 py-2 text-xs font-medium ${textMuted} uppercase`}>Value</th>
                          <th className={`text-center px-5 py-2 text-xs font-medium ${textMuted} uppercase w-16`}>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(info.environment || [])
                          .filter(e => !envSearch || e.key.toLowerCase().includes(envSearch.toLowerCase()))
                          .map(env => (
                          <tr key={env.key} className={`border-b ${border} last:border-0`}>
                            <td className="px-5 py-2 font-mono text-xs text-blue-400">{env.key}</td>
                            <td className="px-5 py-2 font-mono text-xs max-w-[400px] truncate">
                              {env.is_secret && !showSecrets ? (
                                <span className="text-muted-foreground">••••••••</span>
                              ) : (
                                <span className="text-muted-foreground">{env.value}</span>
                              )}
                            </td>
                            <td className="px-5 py-2 text-center">
                              {env.is_secret && (
                                <Badge className="text-[10px] bg-red-500/10 text-red-400 border-red-500/20 border">secret</Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* VERSIONS TAB */}
            {activeTab === 'versions' && info && (
              <>
                {/* Record New Version */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-4 flex items-center gap-2`}>
                    <Upload className="w-4 h-4" /> Record New Version
                  </h2>
                  <div className="flex items-end gap-3">
                    <div className="flex-1 space-y-1.5">
                      <label className={`text-xs ${textMuted}`}>Version Number</label>
                      <Input placeholder="e.g. 2.1.0" value={newVersion} onChange={e => setNewVersion(e.target.value)}
                        className={`h-9 ${isDark ? 'bg-white/[0.03] border-white/10 text-white' : ''}`} data-testid="new-version-input" />
                    </div>
                    <div className="flex-1 space-y-1.5">
                      <label className={`text-xs ${textMuted}`}>Release Notes</label>
                      <Input placeholder="Brief description of changes..." value={versionNotes} onChange={e => setVersionNotes(e.target.value)}
                        className={`h-9 ${isDark ? 'bg-white/[0.03] border-white/10 text-white' : ''}`} data-testid="version-notes-input" />
                    </div>
                    <Button size="sm" onClick={recordVersion} disabled={versionSaving}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-9" data-testid="record-version-btn">
                      {versionSaving ? <RefreshCw className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                      Record
                    </Button>
                  </div>
                </div>

                <div className={`rounded-xl border ${border} ${cardBg} overflow-hidden`}>
                  <div className={`px-5 py-3 border-b ${border}`}>
                    <h2 className={`text-sm font-medium ${textMuted} flex items-center gap-2`}>
                      <History className="w-4 h-4" /> Version History
                    </h2>
                  </div>
                  <div className="px-5 py-3">
                    {(info.version_history || []).length > 0 ? (
                      <div className="space-y-0">
                        {(info.version_history || []).map((v, i) => (
                          <div key={i} className={`flex items-center gap-3 py-3 ${i < (info.version_history || []).length - 1 ? `border-b ${border}` : ''}`}>
                            <div className="relative">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                v.status === 'active' ? 'bg-emerald-500/10' : 'bg-muted0/10'
                              }`}>
                                {v.status === 'active' ? (
                                  <CheckCircle className="w-4 h-4 text-emerald-400" />
                                ) : (
                                  <History className="w-4 h-4 text-muted-foreground" />
                                )}
                              </div>
                              {i < (info.version_history || []).length - 1 && (
                                <div className={`absolute top-8 left-1/2 -translate-x-1/2 w-px h-6 ${
                                  v.status === 'active' ? 'bg-emerald-500/20' : 'bg-card'
                                }`} />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-medium">v{v.version}</span>
                                {v.status === 'active' && (
                                  <Badge className="text-[10px] bg-emerald-500/10 text-emerald-400 border-emerald-500/20 border">current</Badge>
                                )}
                              </div>
                              <p className={`text-xs ${textMuted}`}>
                                {v.deployed_at ? new Date(v.deployed_at).toLocaleString() : v.deployed_at}
                                {v.deployed_by ? ` by ${v.deployed_by}` : ''}
                              </p>
                              {v.notes && <p className="text-xs text-muted-foreground mt-0.5">{v.notes}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <History className={`w-8 h-8 mx-auto mb-2 ${textMuted} opacity-30`} />
                        <p className={`text-sm ${textMuted}`}>No version history recorded</p>
                        <p className={`text-xs ${textMuted} mt-1`}>Versions are tracked when you deploy via ./scripts/deploy.sh</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Deployment Workflow */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-4 flex items-center gap-2`}>
                    <Zap className="w-4 h-4" /> Deployment Workflow
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {[
                      { step: '1', label: 'Push to GitHub', color: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
                      { step: '2', label: 'SSH into VPS', color: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
                      { step: '3', label: './scripts/deploy.sh', color: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
                      { step: '4', label: 'Auto: Backup DB', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
                      { step: '5', label: 'Auto: Build & Migrate', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
                      { step: '6', label: 'Auto: Health Check', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' },
                    ].map((s, i) => (
                      <React.Fragment key={s.step}>
                        <div className={`px-3 py-2 rounded-lg border ${s.color} text-xs font-medium`}>
                          <span className="font-bold mr-1">{s.step}.</span> {s.label}
                        </div>
                        {i < 5 && <ChevronRight className={`w-4 h-4 ${textMuted}`} />}
                      </React.Fragment>
                    ))}
                  </div>
                  <div className={`mt-4 p-3 rounded-lg ${statBg} border ${border}`}>
                    <p className={`text-xs ${textMuted}`}>
                      <strong>Rollback:</strong> If something goes wrong, run <code className="text-blue-300">./scripts/rollback.sh</code> to revert to the previous git commit and rebuild.
                      Database backups are auto-created before each deploy and kept for 30 days.
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* REPORTS TAB */}
            {activeTab === 'reports' && (
              <>
                {/* Send Report Card */}
                <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                  <h2 className={`text-sm font-medium ${textMuted} mb-3 flex items-center gap-2`}>
                    <Mail className="w-4 h-4" /> Weekly Health Report
                  </h2>
                  <p className={`text-xs ${textMuted} mb-4`}>
                    A comprehensive health &amp; monitoring report is automatically emailed every <strong>Monday at 8:00 AM UTC</strong>. Enter recipients below and send on-demand or let it auto-send weekly.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className={`text-xs font-medium ${textMuted} mb-1 block`}>Recipients (comma-separated)</label>
                      <Input value={reportRecipients} onChange={e => setReportRecipients(e.target.value)}
                        placeholder="info@spillconsult.com, admin@company.com"
                        className={`h-9 ${isDark ? 'bg-white/[0.03] border-white/10 text-white' : ''}`}
                        data-testid="report-recipients-input" />
                    </div>
                    <div className="flex items-center gap-3">
                      <Button onClick={sendHealthReport} disabled={sendingReport}
                        className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-health-report-btn">
                        {sendingReport ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                        Send Report Now
                      </Button>
                    <Button variant="outline" onClick={previewReport} data-testid="preview-health-report-btn">
                      <Eye className="w-4 h-4 mr-2" /> Preview Data
                    </Button>
                  </div>
                  </div>
                </div>

                {/* Report Preview */}
                {reportPreview && (
                  <div className={`rounded-xl border ${border} ${cardBg} p-5`}>
                    <h2 className={`text-sm font-medium ${textMuted} mb-4 flex items-center gap-2`}>
                      <Activity className="w-4 h-4" /> Report Preview
                      <Badge variant="secondary" className="text-[10px] ml-auto">{reportPreview.report_period}</Badge>
                    </h2>

                    {/* Alerts */}
                    <div className="mb-4">
                      <h3 className={`text-xs font-medium ${textMuted} mb-2`}>Alerts</h3>
                      <div className="space-y-1">
                        {(reportPreview.alerts || []).map((a, i) => (
                          <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${
                            a.level === 'critical' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                            a.level === 'warning' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                            a.level === 'ok' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' :
                            'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                          }`}>
                            {a.level === 'ok' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                            {a.message}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {[
                        { label: 'CPU', value: `${reportPreview.system_health?.cpu_percent}%`, sub: 'Usage' },
                        { label: 'Memory', value: `${reportPreview.system_health?.memory_percent}%`, sub: `${reportPreview.system_health?.memory_used_gb}GB / ${reportPreview.system_health?.memory_total_gb}GB` },
                        { label: 'Disk', value: `${reportPreview.system_health?.disk_percent}%`, sub: `${reportPreview.system_health?.disk_used_gb}GB / ${reportPreview.system_health?.disk_total_gb}GB` },
                        { label: 'Uptime', value: `${reportPreview.system_health?.uptime_days}d`, sub: `${reportPreview.system_health?.uptime_hours}h` },
                      ].map(s => (
                        <div key={s.label} className={`p-3 rounded-lg ${statBg} border ${border}`}>
                          <p className={`text-[10px] uppercase tracking-wider ${textMuted}`}>{s.label}</p>
                          <p className="text-xl font-bold">{s.value}</p>
                          <p className={`text-[10px] ${textMuted}`}>{s.sub}</p>
                        </div>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                      {[
                        { label: 'EMS Exercises', value: reportPreview.exercise_summary?.total, sub: `${reportPreview.exercise_summary?.active} active` },
                        { label: 'Injects', value: reportPreview.exercise_summary?.total_injects, sub: `${reportPreview.exercise_summary?.triggered_injects} triggered` },
                        { label: 'MP Posts', value: reportPreview.media_portal?.total_posts, sub: `${reportPreview.media_portal?.posts_this_week} this week` },
                        { label: 'Users', value: reportPreview.user_activity?.total_users, sub: `${reportPreview.user_activity?.logins_this_week} logins (7d)` },
                      ].map(s => (
                        <div key={s.label} className={`p-3 rounded-lg ${statBg} border ${border}`}>
                          <p className={`text-[10px] uppercase tracking-wider ${textMuted}`}>{s.label}</p>
                          <p className="text-xl font-bold">{s.value}</p>
                          <p className={`text-[10px] ${textMuted}`}>{s.sub}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default HubSystemPage;
